// Text processing utilities

// Image mapping for local assets
import ackOnSvg from "../assets/ack-on.svg";
import cameraOnSvg from "../assets/camera-on.svg";
import checkOnSvg from "../assets/check-on.svg";
import flushOnSvg from "../assets/flush-on.svg";
import irrigationOnSvg from "../assets/irrigation-on.svg";
import cassetteLockSvg from "../assets/cassette-lock.svg";
import dwnUpSvg from "../assets/DWN up.svg";
import upUpSvg from "../assets/UP up.svg";
import { CHAT_CONSTANTS } from "../utils/constants";

const imageAssets: { [key: string]: string } = {
  "ack-on.svg": ackOnSvg,
  "camera-on.svg": cameraOnSvg,
  "check-on.svg": checkOnSvg,
  "flush-on.svg": flushOnSvg,
  "irrigation-on.svg": irrigationOnSvg,
  "cassette-lock.svg": cassetteLockSvg,
  "DWN up.svg": dwnUpSvg,
  "UP up.svg": upUpSvg,
};

/**
 * Process custom tags in text
 * @param text - The text to process
 * @returns The processed text
 */
export function processCustomTags(text: string) {
  // Fix malformed closing tags [\ -> [/]
  text = text.replace(/\[\\/g, "[/");
  // Remove content between [a] and [/a] tags
  text = text.replace(/\[a\].*?\[\/a\]/g, "");
  // Remove content between [c] and [/c] tags
  text = text.replace(/\[c\].*?\[\/c\]/g, "");
  // Remove [s] and [/s] tags but keep their content
  text = text.replace(/\[s\]/g, "").replace(/\[\/s\]/g, "");
  return text;
}

/**
 * Formats a date time string to a localized string representation
 * @param dateTimeString - The date time string to format
 * @param options - Optional Intl.DateTimeFormatOptions for custom formatting
 * @returns Formatted date time string or "Unknown" if invalid
 */
export function formatDateTime(
  dateTimeString?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateTimeString) return "Unknown";

  try {
    const date = new Date(dateTimeString);
    return date.toLocaleString(undefined, options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateTimeString;
  }
}

/**
 * Enhanced formatting for message content including bold text (**text**), images, and spans
 * @param content - The content to format
 * @param isReadOnly - Whether the content is in read-only mode (affects cursor style for links)
 * @returns Formatted HTML content
 */
export function formatMessageContent(
  content: string,
  isReadOnly: boolean = false
): string {
  // Enhanced formatting for bold text (**text**), images, and spans
  let formattedContent = processCustomTags(content);

  // Handle bold text
  formattedContent = formattedContent.replace(
    /\*\*(.*?)\*\*/g,
    "<strong>$1</strong>"
  );

  // Handle image tags
  formattedContent = formattedContent.replace(
    /<img\s+src="([^"]*)"\s+alt="([^"]*)"[^>]*>/g,
    (_, src, alt) => {
      // Extract image name from URL (everything after the last /)
      const imageName = src.split("/").pop() || src;

      // Check if the image is a local asset
      const localAsset = imageAssets[imageName];
      const imageSrc = localAsset || src;
      return `<img src="${imageSrc}" alt="${alt}" style="width: 20px; height: 20px; margin: 0 2px; vertical-align: middle; display: inline-block;" />`;
    }
  );

  // Handle span tags with custom links, show a small 'pop out' arrow for special data-info
  formattedContent = formattedContent.replace(
    /<span\s+class="custom-link"\s+data-id="([^"]*)"\s+data-info="([^"]*)"[^>]*>(.*?)<\/span>/g,
    (_, dataId, dataInfo, innerText) => {
      const linkStyle = `color: #46ACC6; text-decoration: underline; ${isReadOnly ? "cursor-default" : "cursor-pointer"};`;
      if (dataInfo === CHAT_CONSTANTS.DEFINITION_ROOT) {
        // Add a small "pop out" arrow SVG next to the text
        return `<span class="custom-link" data-id="${dataId}" data-info="${dataInfo}" style="${linkStyle}">${innerText}<svg aria-label="Pop-out" style="display:inline-block; position: relative; top: -0.55em; margin-left: 2px; width: 0.75em; height: 0.75em; vertical-align: baseline;" fill="#46ACC6" viewBox="0 0 36 36" version="1.1" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path class="clr-i-outline clr-i-outline-path-1" d="M27,33H5a2,2,0,0,1-2-2V9A2,2,0,0,1,5,7H15V9H5V31H27V21h2V31A2,2,0,0,1,27,33Z"></path><path class="clr-i-outline clr-i-outline-path-2" d="M18,3a1,1,0,0,0,0,2H29.59L15.74,18.85a1,1,0,1,0,1.41,1.41L31,6.41V18a1,1,0,0,0,2,0V3Z"></path><rect x="0" y="0" width="36" height="36" fill-opacity="0"></rect></g></svg></span>`;
      }
      return `<span class="custom-link" data-id="${dataId}" data-info="${dataInfo}" style="${linkStyle}">${innerText}</span>`;
    }
  );

  return formattedContent;
}
